import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { JsonLd } from "~/components/seo/JsonLd";
import {
  type SalaryRegionData,
  type RelatedOccupation,
} from "~/lib/salary-seo-queries";
import {
  formatSalary,
  slugify,
  generateHreflangLinks,
  BUNDESLAND_SLUGS,
  BUNDESLAND_LABELS,
} from "~/lib/seo-utils";
import {
  SalaryRangeBar,
  SkillLevelComparison,
  SalaryCTA,
  SalaryBreadcrumb,
  SalaryAttribution,
} from "~/components/seo/SalaryCard";
import { serializeForServer } from "~/lib/server-serialization";

const fetchSalaryByRegion = createServerFn({ method: "GET" })
  .validator(z.object({ beruf: z.string(), bundesland: z.string() }))
  .handler(
  async ({ data }): Promise<SalaryRegionData & { related: RelatedOccupation[]; regionLabel: string }> => {
    if (!BUNDESLAND_SLUGS[data.bundesland]) throw notFound();
    const { getSalaryByOccupationAndRegion, getRelatedOccupations } = await import("~/lib/salary-seo-queries");
    const salaryData = await getSalaryByOccupationAndRegion(data.beruf, data.bundesland);
    if (!salaryData) throw notFound();
    const related = await getRelatedOccupations(
      salaryData.occupation.escoUri,
      salaryData.occupation.occupationCode,
    );
    const regionCode = BUNDESLAND_SLUGS[data.bundesland];
    const regionLabel = BUNDESLAND_LABELS[regionCode]?.de ?? data.bundesland;
    return serializeForServer({ ...salaryData, related, regionLabel });
  },
);

export const Route = createFileRoute("/gehalt/$beruf/$bundesland")({
  loader: ({ params }) => fetchSalaryByRegion({ data: { beruf: params.beruf, bundesland: params.bundesland } }),
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const name = loaderData.occupation.occupationName;
    const regionLabel = loaderData.regionLabel;
    const median = loaderData.salaries[0]?.medianMonthlyGross;
    const year = loaderData.salaries[0]?.year ?? 2026;
    const slug = loaderData.occupation.occupationName ? slugify(loaderData.occupation.occupationName) : "";
    const bundeslandSlug = Object.entries(BUNDESLAND_SLUGS).find(([, code]) => BUNDESLAND_LABELS[code]?.de === regionLabel)?.[0] ?? "";
    const description = median
      ? `${name} in ${regionLabel}: Median ${formatSalary(median)} brutto/Monat.`
      : `Gehaltsinformationen für ${name} in ${regionLabel}.`;
    const pagePath = `/gehalt/${slug}/${bundeslandSlug}`;
    return {
      meta: [
        { title: `Gehalt als ${name} in ${regionLabel} (${year}) | Alchema` },
        { name: "description", content: description },
        { property: "og:title", content: `Gehalt als ${name} in ${regionLabel} (${year}) | Alchema` },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `https://alchema.eu${pagePath}` },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: `Gehalt ${name} in ${regionLabel} | Alchema` },
        { name: "twitter:description", content: description },
        { name: "robots", content: "index, follow" },
      ],
      links: generateHreflangLinks(pagePath),
    };
  },
  component: GehaltBerufBundeslandPage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-4xl px-6 py-20 text-center">
      <h1 className="text-2xl font-bold" style={{ color: "var(--text)" }}>
        Keine Daten verfügbar
      </h1>
      <p className="mt-3 text-sm" style={{ color: "var(--text-secondary)" }}>
        Für diese Kombination aus Beruf und Bundesland liegen keine Gehaltsdaten vor.
      </p>
      <Link
        to="/gehalt"
        className="mt-6 inline-flex rounded-[10px] px-5 py-2.5 text-sm font-medium text-white"
        style={{ background: "var(--accent)" }}
      >
        Alle Berufe ansehen
      </Link>
    </div>
  ),
});

function GehaltBerufBundeslandPage() {
  const data = Route.useLoaderData();
  const { occupation, salaries, related, regionLabel } = data;
  const year = salaries[0]?.year ?? 2026;
  const overall = salaries.find(
    (s: { skillLevel: string | null }) => !s.skillLevel || s.skillLevel === "Gesamt",
  );
  const median = overall?.medianMonthlyGross ?? salaries[0]?.medianMonthlyGross ?? 0;
  const q25 = overall?.q25MonthlyGross ?? salaries[0]?.q25MonthlyGross ?? 0;
  const q75 = overall?.q75MonthlyGross ?? salaries[0]?.q75MonthlyGross ?? 0;
  const occupationSlug = slugify(occupation.occupationName);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Occupation",
    name: occupation.occupationName,
    description: occupation.descriptionDe ?? `Gehaltsinformationen für ${occupation.occupationName} in ${regionLabel}`,
    occupationLocation: {
      "@type": "State",
      name: regionLabel,
      containedInPlace: { "@type": "Country", name: "Deutschland" },
    },
    estimatedSalary: {
      "@type": "MonetaryAmountDistribution",
      name: "Brutto-Monatsgehalt",
      currency: "EUR",
      duration: "P1M",
      median,
      percentile25: q25 || undefined,
      percentile75: q75 || undefined,
    },
  };

  return (
    <div style={{ backgroundColor: "var(--bg)" }}>
      <JsonLd json={JSON.stringify(jsonLd)} />
      <section className="mx-auto max-w-4xl px-6 pb-24 pt-20">
        <SalaryBreadcrumb
          items={[
            { label: "Gehalt", href: "/gehalt" },
            { label: occupation.occupationName, href: `/gehalt/${occupationSlug}` },
            { label: regionLabel },
          ]}
        />

        <h1 className="mt-6 text-3xl font-bold tracking-tight" style={{ color: "var(--text)" }}>
          Gehalt als {occupation.occupationName} in {regionLabel} ({year})
        </h1>

        {occupation.descriptionDe && (
          <p className="mt-3 max-w-xl text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {occupation.descriptionDe}
          </p>
        )}

        {median > 0 && (
          <div className="mt-8 rounded-xl border p-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
            <p className="text-xs font-medium" style={{ color: "var(--text-muted)" }}>
              Median Brutto-Monatsgehalt in {regionLabel}
            </p>
            <p className="mt-1 text-3xl font-bold" style={{ color: "var(--text)" }}>
              {formatSalary(median)}
            </p>
            {q25 > 0 && q75 > 0 && (
              <div className="mt-6 pb-6">
                <SalaryRangeBar q25={q25} median={median} q75={q75} />
              </div>
            )}
            {overall?.sampleSize && (
              <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                Stichprobengröße: {overall.sampleSize.toLocaleString("de-DE")}
              </p>
            )}
          </div>
        )}

        {salaries.length > 1 && (
          <div className="mt-8">
            <h2 className="mb-4 text-lg font-semibold" style={{ color: "var(--text)" }}>
              Gehalt nach Qualifikationsstufe
            </h2>
            <SkillLevelComparison rows={salaries} />
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-4">
          <Link
            to="/gehalt/$beruf"
            params={{ beruf: occupationSlug }}
            className="text-sm font-medium hover:underline"
            style={{ color: "var(--accent)" }}
          >
            ← Bundesweiter Vergleich
          </Link>
        </div>

        <div className="mt-10">
          <SalaryCTA />
        </div>

        {related.length > 0 && (
          <div className="mt-10">
            <h2 className="mb-4 text-lg font-semibold" style={{ color: "var(--text)" }}>
              Verwandte Berufe
            </h2>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {related.map((r: RelatedOccupation) => (
                <Link
                  key={r.slug}
                  to="/gehalt/$beruf"
                  params={{ beruf: r.slug }}
                  className="flex items-center justify-between rounded-xl border px-4 py-3 transition hover:shadow-sm"
                  style={{ borderColor: "var(--border)", background: "var(--surface)" }}
                >
                  <span className="text-sm font-medium" style={{ color: "var(--text)" }}>
                    {r.occupationName}
                  </span>
                  <span className="text-sm font-semibold" style={{ color: "var(--accent)" }}>
                    {formatSalary(r.medianMonthlyGross)}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10">
          <SalaryAttribution year={year} />
        </div>
      </section>
    </div>
  );
}
